// src/features/apps/StoreAccountUtils.ts
//
// Pure helpers for the delegated store-account surface (4.2; build spec
// ring2-readiness §5): which row covers an app, what the readiness chip says,
// which submission edges a holder may record, and the B5 download gates.
// The api and daemon stay the authority on every write.

import {
  FACTORY_HOLDER_SUBMISSION_FROM_STATES,
  FACTORY_STORE_ACCOUNT_STATE_COLORS,
  FACTORY_STORE_ACCOUNT_STATE_LABELS,
  FACTORY_STORE_INFO,
  FACTORY_STORE_STATE_GUIDANCE,
  FACTORY_SUBMISSION_STATE_GUIDANCE,
  type FactoryStoreKey,
} from '@/lib/constants/appFactory';
import { APP_PLATFORM_INFO, APP_PLATFORM_KEYS } from '@/lib/constants/appPlatform';
import type { AppCfgsDto } from '@/lib/types/App';
import type { FactoryBuild, FactoryStoreAccount, FactorySubmission } from '@/lib/types/AppFactory';
import { AppPlatformUtils } from './AppPlatformUtils';
import { legalNextStates, type SubmissionState } from './operator/submissionStates';

export interface StoreReadinessChip {
  label: string;
  color: string;
  /** The account row behind the chip, when one exists. */
  account: FactoryStoreAccount | null;
}

export class StoreAccountUtils {
  /**
   * A store that publishes every app from the OPERATOR's own developer account
   * (Google Play, FACTORY_STORE_INFO.operatorAccount): no customer account, no
   * invite, no holder path — every submission is handled for the customer.
   */
  static isOperatorAccountStore(store: string): boolean {
    return !!FACTORY_STORE_INFO[store as FactoryStoreKey]?.operatorAccount;
  }

  /**
   * Stores this app onboards: Roku always; another store once the app has the
   * platform ROW that ships to it (Fire TV → Amazon, Google TV → Google Play) —
   * the row, not the bare entitlement, is what makes a store worth listing.
   */
  static storesForApp(app: AppCfgsDto): FactoryStoreKey[] {
    const stores: FactoryStoreKey[] = ['roku'];
    for (const key of APP_PLATFORM_KEYS) {
      const info = APP_PLATFORM_INFO[key];
      if (AppPlatformUtils.rowFor(app, info.typeId) && !stores.includes(info.store)) {
        stores.push(info.store);
      }
    }
    return stores;
  }

  /**
   * The account covering `cId` on `store`: the account's OWN row (any state)
   * wins; otherwise the nearest parent row that covers its sub-accounts, in
   * the order the parent chain is given. Mirrors the daemon's resolve.
   */
  static resolve(
    rows: FactoryStoreAccount[],
    cId: number,
    parentCIds: number[],
    store: string,
  ): FactoryStoreAccount | null {
    const own = rows.find((r) => r.cId === cId && r.store === store);
    if (own) return own;
    for (const parent of parentCIds) {
      const hit = rows.find((r) => r.cId === parent && r.store === store && r.coversSubAccounts);
      if (hit) return hit;
    }
    return null;
  }

  /**
   * The Readiness card's "To submit" chip for one store; `operatorName` = who runs the
   * platform for this account (useHelpTokens). A store published from the
   * operator's own account (`store` given and operatorAccount) has nothing to
   * set up: the chip says who publishes, whatever rows exist.
   */
  static readinessChip(
    account: FactoryStoreAccount | null,
    operatorName: string,
    store?: string,
  ): StoreReadinessChip {
    if (store && StoreAccountUtils.isOperatorAccountStore(store)) {
      return { label: `Published by ${operatorName}`, color: 'green', account: null };
    }
    if (!account) return { label: 'Not set up', color: 'gray', account: null };
    const base = FACTORY_STORE_ACCOUNT_STATE_LABELS[account.state] ?? account.state;
    const color = FACTORY_STORE_ACCOUNT_STATE_COLORS[account.state] ?? 'gray';
    if (account.state === 'active') {
      const who = account.submitter === 'holder' ? 'you submit' : `delegated to ${operatorName}`;
      return { label: `${base} · ${who}`, color, account };
    }
    return { label: base, color, account };
  }

  /** What a submission state means on one store: the store's own wording, else the shared line. */
  static stateGuidance(state: string, store: string): string {
    return (
      FACTORY_STORE_STATE_GUIDANCE[store]?.[state] ?? FACTORY_SUBMISSION_STATE_GUIDANCE[state] ?? ''
    );
  }

  /**
   * A delegated account on a store whose "Ready to submit" is recorded by the
   * build system (Amazon): the operator submits the update, so that state asks
   * nothing of the customer — it is not an action-needed item. A store published
   * from the operator's own account (Google Play) is handled whatever rows exist.
   */
  static isOperatorHandled(
    account: FactoryStoreAccount | null,
    store: string,
    state: string,
  ): boolean {
    if (state !== 'awaiting_customer') return false;
    if (StoreAccountUtils.isOperatorAccountStore(store)) return true;
    return (
      !!FACTORY_STORE_INFO[store as FactoryStoreKey]?.readyIsAutomatic &&
      account?.state === 'active' &&
      account.submitter !== 'holder'
    );
  }

  /**
   * Whether the store's FIRST version has left the console: any submission of
   * the store past Ready to submit (submitting, in review, decided, live or
   * superseded) proves the app exists in the store's console. Gates the
   * first-submission card of a platform the store knows by its package name
   * (APP_PLATFORM_INFO.storeIdentityIsPackage), which has no store app id to
   * record.
   */
  static firstSubmissionDone(submissions: FactorySubmission[], store: string): boolean {
    return submissions.some(
      (s) => s.store === store && s.state !== 'preparing' && s.state !== 'awaiting_customer',
    );
  }

  /**
   * The "who submits" line on a submission card (B1); `operatorName` per
   * useHelpTokens. `submission` (store + state) lets a store whose ready state
   * is automatic say that nothing is asked of the customer.
   */
  static whoSubmits(
    account: FactoryStoreAccount | null,
    operatorName: string,
    storeLabel: string,
    submission?: { store: string; state: string },
  ): string {
    if (submission && StoreAccountUtils.isOperatorAccountStore(submission.store)) {
      return `${operatorName} publishes this update to ${storeLabel} for you — nothing to do.`;
    }
    if (!account) {
      return `No ${storeLabel} developer account is set up yet — the one-time step every store needs.`;
    }
    switch (account.state) {
      case 'pending':
        return `Your ${storeLabel} developer account is recorded; the invite has not been sent yet.`;
      case 'invited':
        return `Your ${storeLabel} invite is recorded — it activates as soon as the delegation lands.`;
      case 'action_needed':
        return `Your ${storeLabel} developer account needs attention before the next submission.`;
      case 'revoked':
        return `The delegation on your ${storeLabel} developer account was revoked; set it up again to submit.`;
      default:
        if (
          submission &&
          StoreAccountUtils.isOperatorHandled(account, submission.store, submission.state)
        ) {
          return `${operatorName} submits this update for you — nothing to do.`;
        }
        return account.submitter === 'holder'
          ? `You submit this from the ${storeLabel} portal and record each step here.`
          : `${operatorName} submits this for you through your ${storeLabel} developer account.`;
    }
  }

  /** The holder may record its own account's invite; activation only when it submits itself. */
  static holderAccountNext(account: FactoryStoreAccount): string | null {
    if (account.state === 'pending') return 'invited';
    if ((account.state === 'invited' || account.state === 'action_needed') && account.submitter === 'holder') {
      return 'active';
    }
    return null;
  }

  /** Submission edges a holder may record from `state` (ruling 7: everything from awaiting_customer on). */
  static holderSubmissionNext(state: string): readonly SubmissionState[] {
    if (!(FACTORY_HOLDER_SUBMISSION_FROM_STATES as readonly string[]).includes(state)) return [];
    return legalNextStates(state);
  }

  /**
   * B5: the package button exists only when the build produced an artifact that
   * is still current — and (3.34.3) never while the submission is still
   * Preparing: on Roku the readiness edge to Ready to submit is the operator's
   * recorded act, and until then the holder has nothing to do with the file
   * (a customer downloaded a package the card said needed nothing, 2026-09-25).
   */
  static canDownloadPkg(
    build: FactoryBuild | null | undefined,
    submissionState?: string,
  ): boolean {
    if (!build?.artifactUri) return false;
    if (submissionState === 'preparing') return false;
    return build.status === 'preflight_passed' || build.status === 'awaiting_customer';
  }

  /** B5: the guide button exists only once pre-flight froze the submission doc. */
  static canDownloadDoc(submission: FactorySubmission): boolean {
    return !!submission.submissionDocUri;
  }

  /**
   * B6: the newer submission that displaced a superseded one — the next
   * submission for the same store that reached live after it (the operator
   * supersedes the old row when the new one goes live).
   */
  static replacedBy(
    superseded: FactorySubmission,
    all: FactorySubmission[],
  ): FactorySubmission | null {
    const later = all
      .filter(
        (s) =>
          s.appSubmissionId !== superseded.appSubmissionId &&
          s.store === superseded.store &&
          s.created > superseded.created &&
          (s.state === 'live' || s.state === 'superseded'),
      )
      .sort((a, b) => (a.created < b.created ? -1 : a.created > b.created ? 1 : 0));
    return later[0] ?? null;
  }
}
